class LangSelector extends HHBaseElement {
  constructor() {
    super();

    this.countrySelector = this.querySelector(".js-country-selector")
    this.languageSelector = this.querySelector(".js-language-selector")
    this.form = this.querySelector("form")
    this.langHiddenInput = this.querySelector(".js-lang")

    // Language the page is currently rendered in, used to prefer it when
    // the language list is refreshed for a newly selected country.
    this.currentLang = this.langHiddenInput?.value

    // Sync the language sub-options with the current country once on load.
    this.toggleLangSelector()
    this.initializeLanguageCombobox()

    // Selection updates the UI only — navigation happens via the Apply button.
    this.bindCountryOptions()
    this.bindLanguageOptions()
    this.bindApplyButton()
  }

  bindCountryOptions() {
    if (!this.countrySelector) return

    // countryChanged is dispatched by custom-dropdown.js's selectOption() after
    // aria-selected is already updated — covers click, Enter/Space, and blur.
    document.addEventListener('countryChanged', () => this.toggleLangSelector())
  }

  bindLanguageOptions() {
    if (!this.languageSelector) return

    // On language selection, keep the hidden input in sync but do NOT navigate.
    this.languageSelector.querySelectorAll('.js-combo-option').forEach((option) => {
      option.addEventListener('click', () => setTimeout(() => {
        const selected = this.form?.querySelector('[aria-selected="true"]')
        if (selected && this.langHiddenInput) {
          this.langHiddenInput.value = selected.dataset.value
        }
      }, 0))
    })
  }

  bindApplyButton() {
    const applyBtn = this.querySelector('.js-apply-country')
    if (!applyBtn) return
    applyBtn.addEventListener('click', () => this.submit())
  }

  // Keep the current language if the destination country supports it, otherwise
  // fall back to that country's default (first configured) language.
  getLanguageForCountry(option) {
    const languages = (option?.dataset?.languages || 'en')
      .split(',')
      .map((lang) => lang.trim().toLowerCase())
      .filter(Boolean)

    if (languages.length === 0) return 'en'

    if (this.currentLang && languages.includes(this.currentLang.toLowerCase())) {
      return this.currentLang.toLowerCase()
    }

    return languages[0]
  }

  // The option for the country the page is currently rendered in.
  getCurrentCountryOption() {
    if (!this.countrySelector || !window.currentCountryCode) return null

    const current = window.currentCountryCode.toUpperCase()
    return [...this.countrySelector.querySelectorAll('.js-combo-option')].find(
      (option) => (option.dataset.isoCode || '').toUpperCase() === current
    ) || null
  }

  toggleLangSelector() {
    const countryOption = this.countrySelector.querySelector('[aria-selected="true"]');
    const countryIso = countryOption?.dataset?.isoCode;
    const languages = (countryOption?.dataset?.languages || 'en').split(',').map(lang => lang.trim().toLowerCase());

    this.languageSelector.querySelectorAll('[role="option"]').forEach(option => {
      const langValue = option.dataset.value.toLowerCase();
      if (languages.includes(langValue)) {
        option.classList.remove("hidden");
      } else {
        option.classList.add("hidden");
      }
    });

    // Prefer the current language if available; otherwise fall back to first visible.
    const visibleOptions = [...this.languageSelector.querySelectorAll('[role="option"]:not(.hidden)')]
    if (visibleOptions.length > 0) {
      this.languageSelector.querySelectorAll('[role="option"]').forEach(opt => {
        opt.setAttribute('aria-selected', 'false');
        opt.classList.remove('option-current');
      });

      const preferred = visibleOptions.find(
        opt => opt.dataset.value.toLowerCase() === (this.currentLang || '').toLowerCase()
      ) || visibleOptions[0]

      preferred.setAttribute('aria-selected', 'true');
      preferred.classList.add('option-current');

      const languageCombobox = this.languageSelector.querySelector('[role="combobox"]');
      if (languageCombobox) {
        languageCombobox.innerHTML = preferred.innerHTML;
      }

      if (this.langHiddenInput) {
        this.langHiddenInput.value = preferred.dataset.value;
      }
    }

    if (countryIso === "US") {
      this.languageSelector.classList.add("hidden")
    } else {
      this.languageSelector.classList.remove("hidden")
    }
  }

  initializeLanguageCombobox() {
    const languageCombobox = this.languageSelector.querySelector('[role="combobox"]');
    const currentSelectedOption = this.languageSelector.querySelector('[aria-selected="true"]:not(.hidden)');

    if (languageCombobox && currentSelectedOption) {
      languageCombobox.innerHTML = currentSelectedOption.innerHTML;
    }
  }

  submit(countryOption, lang) {
    const option = countryOption || this.countrySelector?.querySelector('[aria-selected="true"]')
    const countryUrl = option?.dataset?.href
    if (!countryUrl) return

    const language = lang
      || this.form?.querySelector('[aria-selected="true"]')?.dataset.value
      || this.langHiddenInput?.value
      || 'en'

    window.location.href = `${countryUrl.replace('en-', `${language}-`)}`;
  }
}

window.customElements.define('lang-selector', LangSelector);
